"use client";

import { useProgress } from "@bprogress/next";
import { useIsFetching } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

type Props = {
  /**
   * First-segment query-key values to track (e.g. `["catalog"]`). The bar runs
   * while any matching query is fetching and stops when they all settle.
   */
  queryKeyPrefixes: string[];
};

/**
 * Drives the top progress bar from TanStack Query fetch state.
 *
 * Some client-side navigations never reach the global `ProgressProvider`:
 * filter changes applied via nuqs only rewrite the URL query — no anchor click,
 * no router push to intercept. The visible work is the query refetch triggered
 * by the changed key. This component mirrors that fetch state onto the bar so
 * the user gets feedback while the background request runs.
 *
 * Domain-agnostic: the caller declares which query keys matter via
 * `queryKeyPrefixes`, so `shared` stays free of any feature knowledge. An
 * SSR-hydrated first load does not refetch, so no bar shows on first paint.
 *
 * Coordination with the global provider: both drive the same single bprogress
 * status (the lib has no ref-count), and the global provider auto-stops the bar
 * on every `[pathname, searchParams]` change — which is exactly when a nuqs
 * filter change fires our refetch.
 */
export function FetchProgress({ queryKeyPrefixes }: Readonly<Props>) {
  const { start, stop } = useProgress();
  const isFetching = useIsFetching({
    predicate: (query) =>
      queryKeyPrefixes.includes(query.queryKey?.[0] as string),
  });
  const isBarActive = useRef(false);

  useEffect(() => {
    if (isFetching > 0 && !isBarActive.current) {
      isBarActive.current = true;
      start();
    } else if (isFetching === 0 && isBarActive.current) {
      isBarActive.current = false;
      stop();
    }
  }, [isFetching, start, stop]);

  // Unmounting mid-fetch would otherwise leave the global bar running forever.
  // Kept in its own unmount-only effect so a changing `isFetching` never
  // restarts the bar (a cleanup on the effect above would churn stop/start).
  useEffect(() => {
    return () => {
      if (isBarActive.current) {
        isBarActive.current = false;
        stop();
      }
    };
  }, [stop]);

  return null;
}
